import { useEffect, useReducer, useMemo } from 'react';
import { clsx } from 'clsx';

import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
  ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import Heading from '@/elements/Heading/Heading.component';
import Button from '@/elements/Button/Button.component';
import {
  IProps,
  SEARCH_ACTION_TYPES,
} from './ComposableContentBlock.interface';
import { mapCardProps } from '@/utils/TranslationComponent/translation.utilities';
import './ComposableContentBlock.styles.css';
import Card from '@/components/Card/Card.component';
import DropdownMenu from '@/elements/Dropdown/Dropdown.component';
import { searchReducer, useFetchResult } from './ComposableContentBlock.utils';
import { cleanHTML } from '@/utils/sanitizeHTML';

export const ComposableContentBlock = (props: IProps) => {
  const {
    title,
    buttonText,
    columns = 'three',
    className,
    categories,
    tagCategories,
    hasSearch = false,
    hasCategory = true,
    placeholder = 'Search',
    noResultMessage = 'No results found',
    noMoreContentMessage = 'No more content available',
    cardStyle = 'grid',
    apiUrl,
    ...blockWrapper
  } = props;

  // Initialize selected index for the single dropdown
  const initialCategoryIndex = categories
    ? categories.items.findIndex((item: any) => item.selected)
    : -1;

  // Initialize selected index for the tag dropdown
  const initialTagCategoryIndex = tagCategories
    ? tagCategories.items.findIndex((item: any) => item.selected)
    : -1;

  const [state, dispatch] = useReducer(searchReducer, {
    openDropdown: false,
    openTagDropdown: false,
    page: 1,
    selectedCategoryIndex: initialCategoryIndex || 0,
    selectedTagCategoryIndex: initialTagCategoryIndex || 0,
    searchKey: '',
    debouncedSearchKey: '',
  });
  const {
    openDropdown,
    openTagDropdown,
    page,
    selectedCategoryIndex,
    selectedTagCategoryIndex,
    searchKey,
    debouncedSearchKey,
  } = state;

  // Updated debounce effect using the reducer
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only update debouncedSearchKey if searchKey has at least 3 characters
      const newDebouncedValue = searchKey.trim().length >= 3 ? searchKey : '';

      // Only dispatch if the value actually changes to avoid unnecessary renders
      if (newDebouncedValue !== debouncedSearchKey) {
        dispatch({
          type: SEARCH_ACTION_TYPES.SET_DEBOUNCED_SEARCH_KEY,
          payload: newDebouncedValue,
        });
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchKey, debouncedSearchKey]); // Build query string with useMemo
  const queryString = useMemo(() => {
    const queryParams: string[] = [];

    if (debouncedSearchKey) {
      queryParams.push(`q=${encodeURIComponent(debouncedSearchKey)}`);
    }

    const categoryValue = categories?.items?.[selectedCategoryIndex]?.value;
    if (categoryValue) {
      queryParams.push(`cat=${encodeURIComponent(categoryValue)}`);
    }

    const tagCategoryValue =
      tagCategories?.items?.[selectedTagCategoryIndex]?.value;
    if (tagCategoryValue) {
      queryParams.push(`tagCat=${encodeURIComponent(tagCategoryValue)}`);
    }

    queryParams.push(`page=${page}`);

    return queryParams.filter(Boolean).join('&');
  }, [
    debouncedSearchKey,
    selectedCategoryIndex,
    selectedTagCategoryIndex,
    categories,
    tagCategories,
    page,
  ]);

  // Call the external hook
  const { status, result } = useFetchResult(apiUrl, queryString, page);

  const onButtonClick = () => {
    // Only increment page if not currently loading
    if (status !== 'loading') {
      dispatch({ type: SEARCH_ACTION_TYPES.NEXT_PAGE });
    }
  };

  return (
    <BlockWrapper
      className={clsx('composableContentBlock', className)}
      {...blockWrapper}
    >
      <ContentWrapper>
        <ContentColumn>
          <div
            className={`composableContentBlock__topContainer ${hasSearch && !hasCategory ? 'composableContentBlock__topContainer--search' : ''}`}
          >
            <Heading
              level={2}
              html={title}
              className="composableContentBlock__title"
            />
            <div className="composableContentBlock__filters">
              {hasCategory && categories && (
                <div className="composableContentBlock__dropdowns">
                  <DropdownMenu
                    modal={true}
                    open={openDropdown}
                    onOpenChange={(value) =>
                      dispatch({
                        type: SEARCH_ACTION_TYPES.SET_OPEN_DROPDOWN,
                        payload: value,
                      })
                    }
                  >
                    <DropdownMenu.Trigger
                      className="composableContentBlock__filter"
                      triggerStyle="bordered"
                    >
                      <span
                        className="w-full text-left"
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            return selectedCategoryIndex >= 0 &&
                              categories.items[selectedCategoryIndex]?.text
                              ? categories.items[selectedCategoryIndex].text
                              : categories.title;
                          })(),
                        }}
                      />
                    </DropdownMenu.Trigger>
                    {openDropdown && (
                      <DropdownMenu.Content>
                        {categories.items.map(
                          (catItem: any, catItemIndex: number) => (
                            <DropdownMenu.Item
                              key={`category-dropdown-item-${catItemIndex}`}
                              onClick={() => {
                                dispatch({
                                  type: SEARCH_ACTION_TYPES.SET_CATEGORY,
                                  payload: catItemIndex,
                                });
                                dispatch({
                                  type: SEARCH_ACTION_TYPES.SET_OPEN_DROPDOWN,
                                  payload: false,
                                });
                              }}
                              dangerouslySetInnerHTML={{
                                __html: cleanHTML(catItem.text),
                              }}
                            />
                          ),
                        )}
                      </DropdownMenu.Content>
                    )}
                  </DropdownMenu>
                </div>
              )}
              {hasCategory && tagCategories && (
                <div className="composableContentBlock__dropdowns">
                  <DropdownMenu
                    modal={true}
                    open={openTagDropdown}
                    onOpenChange={(value) =>
                      dispatch({
                        type: SEARCH_ACTION_TYPES.SET_OPEN_TAG_DROPDOWN,
                        payload: value,
                      })
                    }
                  >
                    <DropdownMenu.Trigger
                      className="composableContentBlock__filter"
                      triggerStyle="bordered"
                    >
                      <span
                        className="w-full text-left"
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            return selectedTagCategoryIndex >= 0 &&
                              tagCategories.items[selectedTagCategoryIndex]
                                ?.text
                              ? tagCategories.items[selectedTagCategoryIndex]
                                  .text
                              : tagCategories.title;
                          })(),
                        }}
                      />
                    </DropdownMenu.Trigger>
                    {openTagDropdown && (
                      <DropdownMenu.Content>
                        {tagCategories.items.map(
                          (tagItem: any, tagItemIndex: number) => (
                            <DropdownMenu.Item
                              key={`tag-category-dropdown-item-${tagItemIndex}`}
                              onClick={() => {
                                dispatch({
                                  type: SEARCH_ACTION_TYPES.SET_TAG_CATEGORY,
                                  payload: tagItemIndex,
                                });
                                dispatch({
                                  type: SEARCH_ACTION_TYPES.SET_OPEN_TAG_DROPDOWN,
                                  payload: false,
                                });
                              }}
                              dangerouslySetInnerHTML={{
                                __html: cleanHTML(tagItem.text),
                              }}
                            />
                          ),
                        )}
                      </DropdownMenu.Content>
                    )}
                  </DropdownMenu>
                </div>
              )}
              {hasSearch && (
                <input
                  className="composableContentBlock__search"
                  onChange={(e) => {
                    dispatch({
                      type: SEARCH_ACTION_TYPES.SET_SEARCH_KEY,
                      payload: e.target.value,
                    });
                  }}
                  placeholder={placeholder}
                  value={searchKey}
                  aria-autocomplete="none"
                  type="text"
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.code === 'enter') {
                      (document.activeElement as HTMLInputElement).blur();
                    }
                  }}
                />
              )}
            </div>
          </div>

          {status === 'loading' && page === 1 ? (
            <div className="message">Loading ...</div>
          ) : status === 'error' ? (
            <div className="message">There was an error fetching the data!</div>
          ) : status === 'no-result' ? (
            <div className="message">{noResultMessage}</div>
          ) : (
            <div
              className={clsx(
                'composableContentBlock__cards',
                `composableContentBlock__cards--${columns}`,
                `composableContentBlock__cards--${cardStyle}`,
              )}
            >
              {result.items?.map((card, idx) => {
                return (
                  <Card
                    key={`card-${idx}`}
                    {...mapCardProps(card)}
                    cta={{
                      ...(mapCardProps(card)?.cta || {}),
                      variant: 'secondary',
                      color: 'primary',
                    }}
                    cardType={cardStyle}
                    className="composableContentBlock__card"
                  />
                );
              })}
            </div>
          )}
          {status === 'no-more-content' && (
            <div className="message">{noMoreContentMessage}</div>
          )}
          {result?.hasNextPage && (
            <Button
              children={status === 'loading' ? '...Loading' : buttonText}
              onClick={onButtonClick}
              disabled={status === 'loading'}
            />
          )}
        </ContentColumn>
      </ContentWrapper>
    </BlockWrapper>
  );
};
